import { useMemo, useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { getStoredUser, getStoredClientId } from "../utils/tokenUtils";
import api from "../services/api";

// Cache for user permissions to avoid repeated API calls
let permissionsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Reset permissions cache (used for testing)
 */
export const resetPermissionsCache = () => {
  permissionsCache = null;
  cacheTimestamp = null;
};

/**
 * Check a single feature/action against effective permissions object (same rules as useCan).
 */
export const checkFeaturePermission = (userPermissions, feature, action) => {
  if (!userPermissions || typeof userPermissions !== "object") return false;
  const featureMapping = {
    user_management: "user",
    role_permission_config: "role",
  };
  const mappedFeature = featureMapping[feature] || feature;
  let featurePerms =
    userPermissions[feature] || userPermissions[mappedFeature] || [];

  if (featurePerms.length === 0) {
    if (feature === "test_suits_cases_sections") {
      featurePerms = userPermissions["test_case_management"] || [];
    } else if (feature === "test_case_management") {
      featurePerms = userPermissions["test_suits_cases_sections"] || [];
    }
  }

  const mappedAction =
    action === "read" ? "view" : action === "write" ? "add_edit" : action;
  return Array.isArray(featurePerms) && featurePerms.includes(mappedAction);
};

/** Pairs [feature, action] — if any pass, user may open Settings (non–chief-admin). */
const SETTINGS_ACCESS_CHECKS = [
  ["configuration", "view"],
  ["configuration", "add_edit"],
  ["user", "view"],
  ["user", "add_edit"],
  ["role", "view"],
  ["role", "add_edit"],
  ["role_permission_config", "view"],
  ["role_permission_config", "write"],
  ["audit_logs", "read"],
  ["audit_logs", "view"],
  ["integration_management", "view"],
  ["integration_management", "add_edit"],
  ["template", "view"],
  ["template", "add_edit"],
];

export const userPermissionsAllowAnySettingsAccess = (userPermissions) =>
  SETTINGS_ACCESS_CHECKS.some(([feature, action]) =>
    checkFeaturePermission(userPermissions, feature, action),
  );

/**
 * Permission hook for RBAC enforcement
 * Fetches permissions from API and checks against user's role
 * @param {string} feature - Feature name (e.g., 'project', 'test_case_management')
 * @param {string} action - Action name (e.g., 'view', 'add_edit', 'delete', 'inactive', 'close')
 * @returns {Object} { can: boolean, loading: boolean, error: Error | null }
 */
export const useCan = (feature, action) => {
  const { user } = useAuth();
  const storedUser = getStoredUser();
  const [userPermissions, setUserPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const effectiveUser = user || storedUser;
  const clientId = getStoredClientId() || effectiveUser?.client_id;

  // Fetch user permissions from API
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!effectiveUser || !clientId) {
        setLoading(false);
        return;
      }

      // Check cache first
      const now = Date.now();
      if (
        permissionsCache &&
        cacheTimestamp &&
        now - cacheTimestamp < CACHE_DURATION
      ) {
        setUserPermissions(permissionsCache);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Fetch effective permissions from backend service
        const response = await api.post("/permission/user-permissions", {
          client_id: clientId,
        });

        if (response.data?.success) {
          permissionsCache = response.data.data?.permissions || {};
          cacheTimestamp = now;
          setUserPermissions(permissionsCache);
        } else {
          // Fallback to default role permissions
          const roleName =
            effectiveUser?.role_name || effectiveUser?.role || null;
          setUserPermissions(getDefaultRolePermissions(roleName));
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching permissions:", err);
        // Fallback to default role permissions on error
        const roleName =
          effectiveUser?.role_name || effectiveUser?.role || null;
        setUserPermissions(getDefaultRolePermissions(roleName));
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [
    effectiveUser?.user_id,
    effectiveUser?.id,
    effectiveUser?.role,
    clientId,
  ]);

  return useMemo(() => {
    // If no user, no permissions
    if (!effectiveUser) {
      return { can: false, loading: false, error: null };
    }

    // Chief admin and admin have all permissions (check both role_name and role for backward compatibility)
    const userRole = effectiveUser.role_name || effectiveUser.role;
    const normalizeRole = (role) =>
      role ? role.toLowerCase().replace(/[_\s-]/g, "-") : null;
    const normalizedRole = normalizeRole(userRole);
    // Chief admin always has all permissions
    if (normalizedRole === "chief-admin") {
      return { can: true, loading: false, error: null };
    }
    // For admin role, check permissions from API (don't auto-grant all, but allow role fallback in components)

    // If still loading permissions, return loading state
    if (loading || !userPermissions) {
      return { can: false, loading: true, error: null };
    }

    const can = checkFeaturePermission(userPermissions, feature, action);

    return { can, loading: false, error };
  }, [effectiveUser, feature, action, userPermissions, loading, error]);
};

/**
 * Whether the user may open the Settings page at all.
 * Chief-admin: always yes (any client). Others: at least one settings-related read/write permission.
 */
export const useSettingsPageAccess = () => {
  const { user } = useAuth();
  const storedUser = getStoredUser();
  const [userPermissions, setUserPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const effectiveUser = user || storedUser;
  const clientId = getStoredClientId() || effectiveUser?.client_id;

  const userRole = effectiveUser?.role_name || effectiveUser?.role;
  const normalizeRole = (role) =>
    role ? role.toLowerCase().replace(/[_\s-]/g, "-") : null;
  const normalizedRole = normalizeRole(userRole);
  const isChiefAdmin = normalizedRole === "chief-admin";

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!effectiveUser || isChiefAdmin) {
        setLoading(false);
        return;
      }
      if (!clientId) {
        setLoading(false);
        return;
      }

      const now = Date.now();
      if (
        permissionsCache &&
        cacheTimestamp &&
        now - cacheTimestamp < CACHE_DURATION
      ) {
        setUserPermissions(permissionsCache);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.post("/permission/user-permissions", {
          client_id: clientId,
        });

        if (response.data?.success) {
          permissionsCache = response.data.data?.permissions || {};
          cacheTimestamp = now;
          setUserPermissions(permissionsCache);
        } else {
          const roleName =
            effectiveUser?.role_name || effectiveUser?.role || null;
          setUserPermissions(getDefaultRolePermissions(roleName));
        }
      } catch (err) {
        console.error("Error fetching permissions (settings access):", err);
        const roleName =
          effectiveUser?.role_name || effectiveUser?.role || null;
        setUserPermissions(getDefaultRolePermissions(roleName));
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [
    effectiveUser?.user_id,
    effectiveUser?.id,
    effectiveUser?.role,
    clientId,
    isChiefAdmin,
  ]);

  return useMemo(() => {
    if (!effectiveUser) {
      return { canAccess: false, loading: false };
    }
    if (isChiefAdmin) {
      return { canAccess: true, loading: false };
    }
    if (!clientId) {
      return { canAccess: false, loading: false };
    }
    if (loading) {
      return { canAccess: false, loading: true };
    }
    if (!userPermissions) {
      return { canAccess: false, loading: false };
    }
    return {
      canAccess: userPermissionsAllowAnySettingsAccess(userPermissions),
      loading: false,
    };
  }, [effectiveUser, isChiefAdmin, clientId, loading, userPermissions]);
};

/**
 * Get default role permissions (fallback)
 */
function getDefaultRolePermissions(role) {
  const defaultPermissions = {
    test_manager: {
      project: ["view", "add_edit", "delete", "inactive", "close"],
      test_case_management: ["view", "add_edit", "delete", "inactive", "close"],
      test_suits_cases_sections: [
        "view",
        "add_edit",
        "delete",
        "inactive",
        "close",
      ],
      test_execution: ["view", "add_edit", "inactive", "close"],
      user: ["view", "add_edit", "delete", "inactive", "close"],
      role: ["view", "add_edit", "inactive", "close"],
      report: ["view", "add_edit", "inactive", "close"],
      dashboard: ["view", "add_edit", "inactive", "close"],
      configuration: ["view", "add_edit", "inactive", "close"],
      template: ["view", "add_edit", "delete", "inactive", "close"],
      runs_plan: ["view", "add_edit", "delete", "inactive", "close"],
      attachments: ["view", "add_edit", "delete", "inactive", "close"],
      test_case_approval: ["view", "add_edit", "inactive", "close"],
      my_activity: ["view", "add_edit", "inactive", "close"],
      generate_test_case_ai: ["view", "add_edit", "inactive", "close"],
      todo: ["view", "add_edit", "delete", "inactive", "close"],
      // Legacy mappings
      user_management: ["view", "add_edit", "delete"],
      role_permission_config: ["view", "add_edit"],
      project_management: ["view", "add_edit", "delete"],
      reports_dashboards: ["view", "add_edit"],
    },
    project_manager: {
      project: ["view", "add_edit", "delete", "inactive", "close"],
      test_case_management: ["view", "add_edit", "delete", "inactive", "close"],
      test_suits_cases_sections: ["view"],
      test_execution: ["view", "add_edit", "inactive", "close"],
      user: ["view", "add_edit", "inactive", "close"],
      role: ["view", "inactive", "close"],
      report: ["view", "add_edit", "inactive", "close"],
      dashboard: ["view", "add_edit", "inactive", "close"],
      configuration: ["view", "add_edit", "inactive", "close"],
      template: ["view", "add_edit", "delete", "inactive", "close"],
      runs_plan: ["view", "add_edit", "delete", "inactive", "close"],
      attachments: ["view", "add_edit", "delete", "inactive", "close"],
      test_case_approval: ["view", "add_edit", "inactive", "close"],
      my_activity: ["view", "add_edit", "inactive", "close"],
      generate_test_case_ai: ["view", "add_edit", "inactive", "close"],
      todo: ["view", "add_edit", "delete", "inactive", "close"],
      // Legacy mappings
      user_management: ["view", "add_edit"],
      role_permission_config: ["view"],
      project_management: ["view", "add_edit", "delete"],
      reports_dashboards: ["view", "add_edit"],
    },
    team_lead: {
      project: ["view", "inactive", "close"],
      test_case_management: ["view", "add_edit", "delete", "inactive", "close"],
      test_suits_cases_sections: [
        "view",
        "add_edit",
        "delete",
        "inactive",
        "close",
      ],
      test_execution: ["view", "add_edit", "inactive", "close"],
      user: ["view", "inactive", "close"],
      role: ["view", "inactive", "close"],
      report: ["view", "inactive", "close"],
      dashboard: ["view", "inactive", "close"],
      configuration: ["view", "inactive", "close"],
      template: ["view", "inactive", "close"],
      runs_plan: ["view", "add_edit", "inactive", "close"],
      attachments: ["view", "add_edit", "delete", "inactive", "close"],
      test_case_approval: ["view", "add_edit", "inactive", "close"],
      my_activity: ["view", "add_edit", "inactive", "close"],
      generate_test_case_ai: ["view", "add_edit", "inactive", "close"],
      todo: ["view", "add_edit", "delete", "inactive", "close"],
      // Legacy mappings
      user_management: ["view"],
      project_management: ["view"],
      reports_dashboards: ["view"],
    },
    qa_engineer: {
      project: ["view", "inactive", "close"],
      test_case_management: ["view", "add_edit", "inactive", "close"],
      test_suits_cases_sections: ["view", "add_edit", "inactive", "close"],
      test_execution: ["view", "add_edit", "inactive", "close"],
      user: ["view", "inactive", "close"],
      role: ["view", "inactive", "close"],
      report: ["view", "inactive", "close"],
      dashboard: ["view", "inactive", "close"],
      configuration: ["view", "inactive", "close"],
      template: ["view", "inactive", "close"],
      runs_plan: ["view", "add_edit", "inactive", "close"],
      attachments: ["view", "add_edit", "inactive", "close"],
      test_case_approval: ["view", "inactive", "close"],
      my_activity: ["view", "add_edit", "inactive", "close"],
      generate_test_case_ai: ["view", "add_edit", "inactive", "close"],
      todo: ["view", "add_edit", "inactive", "close"],
      // Legacy mappings
      project_management: ["view"],
      reports_dashboards: ["view"],
    },
    // New roles: lead, admin, tester
    lead: {
      project: ["view", "add_edit", "delete", "inactive", "close"],
      test_case_management: ["view", "add_edit", "delete", "inactive", "close"],
      test_suits_cases_sections: [
        "view",
        "add_edit",
        "delete",
        "inactive",
        "close",
      ],
      test_execution: ["view", "add_edit", "delete", "inactive", "close"],
      user: ["view", "add_edit", "delete", "inactive", "close"],
      role: ["view", "add_edit", "delete", "inactive", "close"],
      report: ["view", "add_edit", "delete", "inactive", "close"],
      dashboard: ["view", "add_edit", "delete", "inactive", "close"],
      configuration: ["view", "add_edit", "delete", "inactive", "close"],
      template: ["view", "add_edit", "delete", "inactive", "close"],
      runs_plan: ["view", "add_edit", "delete", "inactive", "close"],
      attachments: ["view", "add_edit", "delete", "inactive", "close"],
      test_case_approval: ["view", "add_edit", "inactive", "close"],
      my_activity: ["view", "add_edit", "inactive", "close"],
      generate_test_case_ai: ["view", "add_edit", "inactive", "close"],
      todo: ["view", "add_edit", "delete", "inactive", "close"],
    },
    admin: {
      project: ["view", "add_edit", "delete", "inactive", "close"],
      test_case_management: ["view", "add_edit", "delete", "inactive", "close"],
      test_suits_cases_sections: [
        "view",
        "add_edit",
        "delete",
        "inactive",
        "close",
      ],
      test_execution: ["view", "add_edit", "delete", "inactive", "close"],
      user: ["view", "add_edit", "delete", "inactive", "close"],
      role: ["view", "add_edit", "delete", "inactive", "close"],
      report: ["view", "add_edit", "delete", "inactive", "close"],
      dashboard: ["view", "add_edit", "delete", "inactive", "close"],
      configuration: ["view", "add_edit", "delete", "inactive", "close"],
      template: ["view", "add_edit", "delete", "inactive", "close"],
      runs_plan: ["view", "add_edit", "delete", "inactive", "close"],
      attachments: ["view", "add_edit", "delete", "inactive", "close"],
      test_case_approval: ["view", "add_edit", "delete", "inactive", "close"],
      my_activity: ["view", "add_edit", "delete", "inactive", "close"],
      generate_test_case_ai: [
        "view",
        "add_edit",
        "delete",
        "inactive",
        "close",
      ],
      todo: ["view", "add_edit", "delete", "inactive", "close"],
    },
    tester: {
      project: ["view", "add_edit", "inactive", "close"],
      test_case_management: ["view", "add_edit", "inactive", "close"],
      test_suits_cases_sections: ["view", "add_edit", "inactive", "close"],
      test_execution: ["view", "add_edit", "inactive", "close"],
      user: ["view", "inactive", "close"],
      role: ["view", "inactive", "close"],
      report: ["view", "add_edit", "delete", "inactive", "close"],
      dashboard: ["view", "add_edit", "inactive", "close"],
      configuration: ["view", "inactive", "close"],
      template: ["view", "inactive", "close"],
      runs_plan: ["view", "add_edit", "inactive", "close"],
      attachments: ["view", "add_edit", "delete", "inactive", "close"],
      test_case_approval: ["view", "inactive", "close"],
      my_activity: ["view", "inactive", "close"],
      generate_test_case_ai: ["view", "inactive", "close"],
      todo: ["view", "inactive", "close"],
    },
  };

  return defaultPermissions[role] || {};
}

/**
 * Hook to check multiple permissions
 * @param {Array<{feature: string, action: string}>} permissions
 * @returns {Object} { can: boolean, loading: boolean }
 */
export const useCanMultiple = (permissions) => {
  // Call all hooks at the top level - React requires hooks to be called in the same order
  // For now, we'll limit to checking permissions one at a time or use a different approach
  // This is a limitation - we can't dynamically call hooks in a loop
  // Alternative: Check permissions synchronously using the same logic as useCan
  const { user } = useAuth();
  const storedUser = getStoredUser();
  const effectiveUser = user || storedUser;

  return useMemo(() => {
    if (!effectiveUser) {
      return { can: false, loading: false };
    }

    // Chief admin has all permissions (check both role_name and role for backward compatibility)
    const userRole = effectiveUser.role_name || effectiveUser.role;
    const normalizeRole = (role) =>
      role ? role.toLowerCase().replace(/[_\s-]/g, "-") : null;
    const normalizedRole = normalizeRole(userRole);
    if (normalizedRole === "chief-admin") {
      return { can: true, loading: false };
    }

    // Check all permissions synchronously
    const rolePermissions = {
      test_manager: {
        user_management: ["read", "write", "assign"],
        role_permission_config: ["read", "write"],
        project_management: ["read", "write"],
        test_case_management: ["read", "write"],
        test_execution: ["read", "write"],
        reports_dashboards: ["read", "write"],
      },
      project_manager: {
        user_management: ["read", "assign"],
        role_permission_config: ["read"],
        project_management: ["read", "write"],
        test_case_management: ["read", "write"],
        test_execution: ["read", "write"],
        reports_dashboards: ["read", "write"],
      },
      team_lead: {
        user_management: ["read"],
        project_management: ["read"],
        test_case_management: ["read", "write"],
        test_execution: ["read", "write"],
        reports_dashboards: ["read"],
      },
      qa_engineer: {
        project_management: ["read"],
        test_case_management: ["read", "write"],
        test_execution: ["read", "write"],
        reports_dashboards: ["read"],
      },
    };

    const rolePerms = rolePermissions[effectiveUser.role] || {};
    const can = permissions.every(({ feature, action }) => {
      const featurePerms = rolePerms[feature] || [];
      return featurePerms.includes(action) || featurePerms.includes("write");
    });

    return { can, loading: false };
  }, [effectiveUser, permissions]);
};

export default useCan;
